import axios from "../utils/index";

export interface EligibleDoctor {
  userId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  displayName?: string;
  profileImage?: string;
  specialities?: string[];
  skillLevel?: string;
  locationCity?: string;
  locationState?: string;
  favorite?: boolean;
  workedBefore?: boolean;
  precredential?: boolean;
  matchScore?: number;
  alreadyInvited?: boolean;
  alreadyApplied?: boolean;
}

const isSuccessCode = (code?: number) => code === 0 || code === 200;

const unwrapList = <T>(response: { code?: number; data?: T; message?: string }) => {
  if (!isSuccessCode(response?.code)) {
    throw new Error(response?.message || "Request failed");
  }
  return (response.data ?? []) as T;
};

export async function fetchEligibleDoctors(
  shiftsId: string,
  q?: string,
  page = 0,
  size = 20,
  hospitalId?: string,
): Promise<EligibleDoctor[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("page", String(page));
  params.set("size", String(size));
  const headers: Record<string, string> = {};
  if (hospitalId) {
    headers["X-Hospital-Id"] = hospitalId;
    headers.accountId = hospitalId;
  }
  const response = await axios.get(
    `admin/v1/hospital/shifts/${shiftsId}/eligible-doctors?${params.toString()}`,
    { headers },
  );
  return unwrapList<EligibleDoctor[]>(response.data);
}

export async function inviteDoctorToShift(
  shiftsId: string,
  userId: string,
  body: { invitationMessage?: string; shiftDayIds?: string[] } = {},
  hospitalId?: string,
) {
  const headers: Record<string, string> = {};
  if (hospitalId) {
    headers["X-Hospital-Id"] = hospitalId;
    headers.accountId = hospitalId;
  }
  try {
    const response = await axios.post(
      `admin/v1/hospital/shifts/${shiftsId}/invite/${userId}`,
      body,
      { headers, timeout: 20000 },
    );
    if (!isSuccessCode(response.data?.code)) {
      throw new Error(response.data?.message || "Invite failed");
    }
    return response.data?.data;
  } catch (err: any) {
    if (err instanceof Error && !err.message.startsWith("Request failed")) {
      throw err;
    }
    throw new Error(
      err?.response?.data?.message || err?.message || "Invite failed",
    );
  }
}

export interface HospitalDoctorCard {
  userId: string;
  firstName?: string;
  lastName?: string;
  profileImage?: string;
  specialty?: string;
  skillLevel?: string;
  headline?: string;
  locationCity?: string;
  locationState?: string;
  favorite?: boolean;
  workedBefore?: boolean;
  hasWorkedAtHospital?: boolean;
  precredential?: boolean;
  canInvite?: boolean;
  permanentInterest?: boolean;
  employmentInterest?: string;
}

export interface HospitalDoctorBrowseResult {
  items: HospitalDoctorCard[];
  page: number;
  size: number;
  hasMore: boolean;
}

export async function browseHospitalDoctors(
  q?: string,
  page = 0,
  size = 30,
  specialty?: string,
  skillLevel?: string,
  relation?: string,
  hospitalId?: string,
): Promise<HospitalDoctorBrowseResult> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (specialty) params.set("specialty", specialty);
  if (skillLevel) params.set("skillLevel", skillLevel);
  if (relation) params.set("relation", relation);
  if (hospitalId) params.set("hospitalId", hospitalId);
  params.set("page", String(page));
  params.set("size", String(size));
  const response = await axios.get(
    `admin/v1/hospital/doctors/browse?${params.toString()}`,
  );
  const payload = unwrapList<HospitalDoctorBrowseResult | HospitalDoctorCard[]>(
    response.data,
  );
  if (Array.isArray(payload)) {
    return {
      items: payload,
      page,
      size,
      hasMore: payload.length >= size,
    };
  }
  return {
    items: payload?.items ?? [],
    page: payload?.page ?? page,
    size: payload?.size ?? size,
    hasMore: Boolean(payload?.hasMore),
  };
}

export interface DoctorHospitalApplication {
  ordersId?: string;
  shiftsId?: string;
  shiftsDaysId?: string;
  shiftsDays?: string;
  specialty?: string;
  skillLevel?: string;
  applyTime?: number;
  status?: string;
  reviewable?: boolean;
}

export function isApplicationReviewable(app: DoctorHospitalApplication): boolean {
  if (app.reviewable === false) return false;
  if (app.reviewable === true) {
    return Boolean(app.shiftsId && app.shiftsDaysId);
  }
  if (!app.shiftsId || !app.shiftsDaysId) return false;
  const status = String(app.status ?? "pending").toLowerCase();
  if (
    status === "archived" ||
    status === "cancelled" ||
    status === "canceled" ||
    status === "closed" ||
    status === "past" ||
    status === "approved" ||
    status === "confirmed"
  ) {
    return false;
  }
  const dayMatch = String(app.shiftsDays ?? "").match(/(\d{4}-\d{2}-\d{2})/);
  if (dayMatch) {
    const shiftDayEnd = new Date(`${dayMatch[1]}T23:59:59`);
    if (!Number.isNaN(shiftDayEnd.getTime()) && shiftDayEnd.getTime() < Date.now()) {
      return false;
    }
  }
  return true;
}

export function applicationStatusLabel(app: DoctorHospitalApplication): string {
  const status = String(app.status ?? "").toLowerCase();
  switch (status) {
    case "confirmed":
    case "approved":
      return "Confirmed";
    case "cancelled":
    case "canceled":
      return "Cancelled";
    case "archived":
      return "Archived";
    case "closed":
      return "Closed";
    case "past":
      return "Past";
    case "pending":
      return "Pending";
    default:
      return "Past";
  }
}

export interface HospitalRelationshipSummary {
  totalApplications?: number;
  approvedShifts?: number;
  lastWorkedTime?: number;
}

export interface DoctorSocialProfile {
  userId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  profileImage?: string;
  medicalDegree?: string;
  specialty?: string;
  specialtyTags?: string[];
  skillLevel?: string;
  headline?: string;
  locationState?: string;
  completedShifts?: number;
  averageRating?: number;
  totalRatings?: number;
  favorite?: boolean;
  workedBefore?: boolean;
  precredential?: boolean;
  canInvite?: boolean;
  sections?: Record<string, unknown>;
  activeApplications?: DoctorHospitalApplication[];
  relationshipSummary?: HospitalRelationshipSummary;
}

export interface EligibleShift {
  shiftsId: string;
  hospitalAccountId?: string;
  hospitalName?: string;
  hospitalImage?: string;
  hospitalLocation?: string;
  specialty?: string;
  skillLevel?: string;
  shiftsDescription?: string;
  nextShiftDayId?: string;
  nextShiftLabel?: string;
  nextStartTime?: number;
  nextEndTime?: number;
  matchScore?: number;
  matchReason?: string;
  alreadyInvited?: boolean;
}

export type EligibleShiftScope = {
  hospitalId?: string;
  networkScope?: boolean;
};

export async function fetchEligibleShiftsForDoctor(
  userId: string,
  q?: string,
  page = 0,
  size = 20,
  scope?: EligibleShiftScope,
): Promise<EligibleShift[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("page", String(page));
  params.set("size", String(size));
  if (scope?.hospitalId) params.set("hospitalId", scope.hospitalId);
  if (scope?.networkScope) params.set("networkScope", "true");
  const response = await axios.get(
    `admin/v1/hospital/doctors/${userId}/eligible-shifts?${params.toString()}`,
  );
  return unwrapList<EligibleShift[]>(response.data);
}

export async function fetchDoctorSocialProfile(
  userId: string,
): Promise<DoctorSocialProfile> {
  const response = await axios.get(
    `admin/v1/hospital/doctors/${userId}/social-profile`,
  );
  if (!isSuccessCode(response.data?.code)) {
    throw new Error(response.data?.message || "Request failed");
  }
  return (response.data?.data ?? {}) as DoctorSocialProfile;
}
